import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { NavLink, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import type { RouteDefinition } from "@apf/framework-config";
import { groundworksRepositoryUrl, partnerLicenseUrl } from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { Lockup } from "./ui";
import { iconFor } from "./icons";
import { SectionNavigation, owningSectionId } from "./SectionNavigation";

const mobileNavigationQuery = "(max-width: 64rem)";

function useNarrowNavigation(): boolean {
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(mobileNavigationQuery).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(mobileNavigationQuery);
    const sync = () => setIsNarrow(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  return isNarrow;
}

type ShellRoute = Pick<RouteDefinition, "id" | "path" | "labelKey">;

interface NavigationEntry {
  route: ShellRoute;
  label: string;
}

interface NavigationGroup {
  id: string;
  labelKey: string;
  entries: NavigationEntry[];
}

function NavigationLinks({ entries, close, currentSectionId }: {
  entries: NavigationEntry[];
  close: () => void;
  /* The owning section of the current route: routes outside the top-nav
   * paths (Workblocks, Design, Connected Work, …) still light their section. */
  currentSectionId?: string;
}) {
  return (
    <>
      {entries.map(({ route, label }) => {
        return (
          <NavLink
            key={route.id}
            to={route.path}
            end={route.path === "/"}
            onClick={close}
            className={({ isActive }) =>
              `apf-primary-navigation__link${isActive || route.id === currentSectionId ? " apf-primary-navigation__link--active" : ""}`
            }
          >
            <span>{label}</span>
          </NavLink>
        );
      })}
    </>
  );
}

const CloseIcon = iconFor("x");
const SearchIcon = iconFor("search");
const ThemeIcon = iconFor("theme");

type ThemePreference = "system" | "dark" | "light";

const themeCycle: ThemePreference[] = ["system", "dark", "light"];
const themeLabels: Record<ThemePreference, string> = { system: "System", dark: "Dark", light: "Light" };
const themeStorageKey = "apf-theme";

function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(themeStorageKey);
    return stored === "dark" || stored === "light" ? stored : "system";
  } catch {
    // Hosted Workblocks can run without same-origin storage permission.
    return "system";
  }
}

function persistThemePreference(theme: ThemePreference): void {
  if (typeof window === "undefined") return;
  try {
    if (theme === "system") window.localStorage.removeItem(themeStorageKey);
    else window.localStorage.setItem(themeStorageKey, theme);
  } catch {
    // The current-session DOM theme still works when persistence is unavailable.
  }
}

/* The theme control: three states, cycled. "System" removes the stamp and
 * follows the OS through the media-guarded token blocks; an explicit choice
 * stamps data-theme on the root so it wins in both directions. The stamp is
 * also applied pre-paint by the inline script in index.html. */
function ThemeToggle() {
  const [theme, setTheme] = useState<ThemePreference>(readThemePreference);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", theme);
    }
    persistThemePreference(theme);
    const dark = theme === "dark"
      || (theme === "system" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    /* The browser chrome should show the canvas a partner theme actually paints,
     * not the core palette. Read the resolved --apf-canvas off the root after the
     * stamp above so the cascade has already chosen the partner value; the core
     * literals stand in only when the property is missing or still unresolved
     * (no stylesheet yet, or an environment without getComputedStyle). */
    const resolvedCanvas = typeof window.getComputedStyle === "function"
      ? window.getComputedStyle(root).getPropertyValue("--apf-canvas").trim()
      : "";
    const themeColour = resolvedCanvas && !resolvedCanvas.includes("var(")
      ? resolvedCanvas
      : (dark ? "#131316" : "#f7f7f8");
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", themeColour);
  }, [theme]);
  const next = themeCycle[(themeCycle.indexOf(theme) + 1) % themeCycle.length]!;
  return (
    <button
      type="button"
      className="apf-theme-toggle"
      onClick={() => setTheme(next)}
      aria-label={`Theme: ${themeLabels[theme]}. Switch to ${themeLabels[next].toLowerCase()}.`}
    >
      <ThemeIcon size={16} />
      <span className="apf-theme-toggle__label">Theme</span>
      <span className="apf-theme-toggle__value">{themeLabels[theme]}</span>
    </button>
  );
}

function HeaderSearch({ route }: { route: ShellRoute }) {
  const copy = useCopy();
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState("");
  const onSearchPage = location.pathname === route.path;

  useEffect(() => {
    if (!onSearchPage) setValue("");
  }, [onSearchPage]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = value.trim();
    navigate(query ? `${route.path}?q=${encodeURIComponent(query)}` : route.path);
  };

  return (
    <form
      className={`apf-header-search${onSearchPage ? " apf-header-search--active" : ""}`}
      role="search"
      aria-label={copy(route.labelKey)}
      onSubmit={submit}
    >
      <SearchIcon size={16} />
      <label className="apf-visually-hidden" htmlFor="apf-header-search-input">{copy(route.labelKey)}</label>
      <input
        id="apf-header-search-input"
        type="search"
        autoComplete="off"
        placeholder={copy(route.labelKey)}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
    </form>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const isNarrowNavigation = useNarrowNavigation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navigationDrawerRef = useRef<HTMLDivElement>(null);
  const navigationCloseRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  /* The sticky ledger: the header stamps its rendered height on the root, and
   * every sticky top, scroll margin and scroll padding derives from it through
   * --apf-sticky-offset. A measured height cannot go stale when a breakpoint
   * moves, a group label appears, or 200% text wraps the band. */
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;
    const apply = () => {
      root.style.setProperty("--apf-header-height", `${Math.ceil(header.getBoundingClientRect().height)}px`);
    };
    apply();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(apply) : undefined;
    observer?.observe(header);
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--apf-header-height");
    };
  }, []);

  const visibleRoutes = snapshot.content.routes;
  const searchRoute = useMemo(() => visibleRoutes.find((route) => route.path === "/search"), [visibleRoutes]);
  const currentSectionId = useMemo(
    () => owningSectionId(visibleRoutes, location.pathname),
    [visibleRoutes, location.pathname],
  );
  /* Navigation order and membership are projected with the route records. The
   * audience resolver removes inaccessible routes before the shell receives the
   * snapshot, and an empty group therefore renders nothing. */
  const navigationGroups = useMemo<NavigationGroup[]>(
    () => {
      const orderedGroups = [...(snapshot.content.primaryNavigationGroups ?? [])]
        .sort((left, right) => left.order - right.order);
      return orderedGroups.flatMap((group) => {
        const entries = visibleRoutes
          .filter((route) => route.primaryNavigation?.groupId === group.id)
          .sort(
            (left, right) =>
              left.primaryNavigation!.order - right.primaryNavigation!.order,
          )
          .map((route) => ({ route, label: copy(route.labelKey) }));
        if (!entries.length) return [];
        return [{ id: group.id, labelKey: group.labelKey, entries }];
      });
    },
    [copy, snapshot.content.primaryNavigationGroups, visibleRoutes]
  );

  useEffect(() => setMobileOpen(false), [location.pathname]);
  /* A followed link opens the new page at its top. Hash navigations keep
   * their own target, Back and Forward keep the browser's restored position,
   * and search-parameter changes on the same path never move the reader.
   * Scrolling is instant, so the reduced-motion preference holds. */
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== "POP" && !location.hash) window.scrollTo(0, 0);
  }, [navigationType, location.pathname, location.hash]);
  useEffect(() => {
    if (!isNarrowNavigation) setMobileOpen(false);
  }, [isNarrowNavigation]);

  const restoreMenuFocus = useCallback(() => {
    const focus = () => menuButtonRef.current?.focus();
    if (typeof window.requestAnimationFrame === "function") window.requestAnimationFrame(focus);
    else focus();
  }, []);

  const dismissMobileNavigation = useCallback(() => {
    setMobileOpen(false);
    restoreMenuFocus();
  }, [restoreMenuFocus]);

  useEffect(() => {
    if (!isNarrowNavigation || !mobileOpen) return;
    const drawer = navigationDrawerRef.current;
    if (!drawer) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    navigationCloseRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismissMobileNavigation();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [dismissMobileNavigation, isNarrowNavigation, mobileOpen]);

  return (
    <div className={`apf-app-shell ${snapshot.theme.className}`}>
      <a className="apf-skip-link" href="#main-content">Skip to main content</a>
      <header className="apf-site-header" ref={headerRef}>
        <div className="apf-site-header__inner">
          <NavLink className="apf-site-header__brand" to="/" end aria-label="GroundWorks home">
            <Lockup mark nameKey="header.brand-name" fallbackName="GroundWorks" />
          </NavLink>
          <div
            id="primary-navigation-drawer"
            ref={navigationDrawerRef}
            className={`apf-navigation-drawer${mobileOpen ? " apf-navigation-drawer--open" : ""}`}
            role={isNarrowNavigation && mobileOpen ? "dialog" : undefined}
            aria-modal={isNarrowNavigation && mobileOpen ? true : undefined}
            aria-label={isNarrowNavigation && mobileOpen ? "Primary navigation" : undefined}
            aria-hidden={isNarrowNavigation && !mobileOpen ? true : undefined}
            inert={isNarrowNavigation && !mobileOpen ? true : undefined}
          >
            <div className="apf-navigation-drawer__header">
              <strong>Menu</strong>
              <button
                ref={navigationCloseRef}
                className="apf-button apf-button--quiet apf-navigation-drawer__close"
                type="button"
                onClick={dismissMobileNavigation}
                aria-label="Close navigation"
              >
                <CloseIcon />
              </button>
            </div>
            <nav className="apf-primary-navigation" aria-label="Primary">
              {navigationGroups.length === 1 ? (
                <NavigationLinks entries={navigationGroups[0]!.entries} close={() => setMobileOpen(false)} currentSectionId={currentSectionId} />
              ) : (
                navigationGroups.map((group) => {
                  const label = copy(group.labelKey);
                  return (
                    <div className="apf-primary-navigation__group" role="group" aria-label={label} key={group.id}>
                      <p className="apf-primary-navigation__group-label" aria-hidden="true">{label}</p>
                      <div className="apf-primary-navigation__group-links">
                        <NavigationLinks entries={group.entries} close={() => setMobileOpen(false)} currentSectionId={currentSectionId} />
                      </div>
                    </div>
                  );
                })
              )}
            </nav>
          </div>
          {searchRoute ? <HeaderSearch route={searchRoute} /> : null}
          <ThemeToggle />
          <button
            ref={menuButtonRef}
            className="apf-button apf-button--quiet apf-mobile-menu-button"
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-controls="primary-navigation-drawer"
            aria-expanded={mobileOpen}
          >
            {/* The visible word is the accessible name; aria-expanded carries
              * the open state, so no label-in-name mismatch. */}
            Menu
          </button>
        </div>
      </header>
      <SectionNavigation />
      {isNarrowNavigation && mobileOpen ? (
        <div
          className="apf-navigation-backdrop"
          aria-hidden="true"
          onClick={dismissMobileNavigation}
        />
      ) : null}
      <div className="apf-app-shell__stage">
        <main id="main-content" tabIndex={-1}>{children}</main>
      </div>
      <footer className="apf-site-footer">
        <div className="apf-site-footer__inner">
          {/* Footer attribution and licence statements are claims, not shell
            * defaults. If governance removes a key from a projection, the
            * statement disappears rather than resurfacing through fallback
            * copy. */}
          {([
            "site.footer.implementation",
            "site.footer.program",
            "site.footer.licensing",
          ] as const).map((key) =>
            snapshot.copy[key] ? <p key={key}>{snapshot.copy[key]}</p> : null,
          )}
          <p className="apf-site-footer__links">
            <a href={partnerLicenseUrl} rel="noreferrer">{copy("site.footer.license-link", "Read the Beyond Work Partner License")}</a>
            <a href={groundworksRepositoryUrl} rel="noreferrer">{copy("site.footer.repo-link", "GroundWorks on GitHub")}</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
